import { ListLayout, Tab, TabGroup, theme } from '@ketch-com/deck'
import React, { FC, useMemo } from 'react'
import { Box, styled } from '@mui/material'
import { AuditLogFilters } from 'pages/insights/auditLog/components/AuditLogFilters'
import { useAuditLogContext } from 'pages/insights/auditLog/context/AuditLogContext'
import { NavigationBreadCrumbs } from 'components/appLayout/appNavigation/breadcrumbs/NavigationBreadCrumbs'
import { RoutesManager } from 'utils/routing/routesManager'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { AuditLogPopup } from 'pages/insights/auditLog/components/AuditLogPopup'
import { NoSearchEmptyState } from 'pages/insights/auditLog/components/NoSearchEmptyState'
import { NoDataEmptyState } from 'pages/insights/auditLog/components/NoDataEmptyState'
import { AuditLogExperienceDrawer } from 'pages/insights/auditLog/components/AuditLogExperienceDrawer'
import { HistoricalPermitPopup } from 'pages/insights/auditLog/components/historicalPermitPopup/HistoricalPermitPopup'
import { ExperiencePreviewVariant } from 'pages/consentAndRights/experiences-v2/view/components/experiencePreview/constants'

const AuditLogContent = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  alignItems: 'center',
  background: theme.palette.White.o100,
  borderTopLeftRadius: '11px',
  borderTopRightRadius: '11px',
  height: '100%',
  padding: '16px 32px 64px 32px',
})

const TabsBox = styled(Box)({
  borderBottom: 1,
  borderColor: 'divider',
  marginBottom: 16,
  transition: 'height 0.4s ease-in-out, opacity 0.4s ease-in-out',
})

const breadcrumbs = [
  { title: 'Reports', link: RoutesManager.insights.root.getURL() },
  { title: 'Audit Log', link: RoutesManager.insights.auditLog.root.getURL() },
]

export const auditLogTabs = [
  {
    id: 'consent',
    label: 'Consent',
    path: RoutesManager.insights.auditLog.consent.root.getURL(),
  },
  {
    id: 'rights',
    label: 'Rights',
    path: RoutesManager.insights.auditLog.rights.root.getURL(),
  },
  {
    id: 'subscriptions',
    label: 'Subscriptions',
    path: RoutesManager.insights.auditLog.subscriptions.root.getURL(),
  },
]

export const AuditLog: FC = () => {
  const {
    showNoSearchState,
    showNoDataState,
    auditLogActionVariant,
    auditLogHistoricalPermitPopupVariant,
    consentData,
    rightsData,
    subscriptionsData,
    isLoading,
    isRefetching,
  } = useAuditLogContext()
  const navigate = useNavigate()
  const { pathname, search } = useLocation()

  const isReady = !isLoading && !isRefetching

  const showLoadingOrEmptyState = showNoSearchState || showNoDataState
  const emptyStateComponent = useMemo(() => {
    if (showNoSearchState) {
      return <NoSearchEmptyState />
    }

    if (showNoDataState) {
      return <NoDataEmptyState />
    }

    return null
  }, [showNoSearchState, showNoDataState])

  const isConsentDisabled = isReady && !consentData.length
  const isRightsDisabled = isReady && !rightsData.length
  const isSubscriptionsDisabled = isReady && !subscriptionsData.length

  const tab = useMemo(() => {
    // Map of disabled flags in the same order as auditLogTabs
    const disabledFlags = [isConsentDisabled, isRightsDisabled, isSubscriptionsDisabled]

    // Find the tab index based on the current URL
    let tabIndex = auditLogTabs.findIndex(t => pathname.includes(t.path))

    // If the tab is disabled or not found, pick the first enabled tab
    if (tabIndex === -1 || disabledFlags[tabIndex]) {
      tabIndex = disabledFlags.findIndex(flag => !flag)
    }

    // If all tabs are disabled, default to 0
    return tabIndex === -1 ? 0 : tabIndex
  }, [pathname, isConsentDisabled, isRightsDisabled, isSubscriptionsDisabled])

  // If the tab was changed due to being disabled or invalid, navigate to the first enabled tab
  React.useEffect(() => {
    // Map of disabled flags in the same order as auditLogTabs
    const disabledFlags = [isConsentDisabled, isRightsDisabled, isSubscriptionsDisabled]
    const urlTabIndex = auditLogTabs.findIndex(t => pathname.includes(t.path))
    // If the tab index in the URL is not the same as the computed tab (and the computed tab is valid), navigate
    if ((urlTabIndex !== tab || disabledFlags[urlTabIndex]) && tab !== -1 && !disabledFlags[tab]) {
      navigate(`${auditLogTabs[tab].path}${search}`, { replace: true })
    }
  }, [tab, pathname, search, navigate, isConsentDisabled, isRightsDisabled, isSubscriptionsDisabled])

  // Navigate to the selected tab and keep the current query params
  const handleTabChange = (_: React.SyntheticEvent, newTab: number) => {
    const clickedTab = auditLogTabs[newTab]
    navigate(`${clickedTab.path}${search}`)
  }

  return (
    <ListLayout headerTitle="Audit Log" actionHeaderBottomBlockComponent={<AuditLogFilters />}>
      <NavigationBreadCrumbs type="light" items={breadcrumbs} />
      <Box>
        {/* Tabs */}
        <TabsBox
          id="tabs-box"
          sx={{
            height: showLoadingOrEmptyState ? '0px' : '42px',
            opacity: showLoadingOrEmptyState ? 0 : 1,
            pointerEvents: showLoadingOrEmptyState ? 'none' : 'auto',
          }}
        >
          <TabGroup value={tab} onChange={handleTabChange}>
            <Tab label="Consent" disabled={!consentData.length} />
            <Tab label="Rights" disabled={!rightsData.length} />
            <Tab label="Subscriptions" disabled={!subscriptionsData.length} />
          </TabGroup>
        </TabsBox>

        {/* Main Content */}
        <AuditLogContent>{emptyStateComponent || <Outlet />}</AuditLogContent>
      </Box>
      {!!auditLogActionVariant && <AuditLogPopup />}
      {!!auditLogHistoricalPermitPopupVariant && (
        <HistoricalPermitPopup variant={auditLogHistoricalPermitPopupVariant} />
      )}
      <AuditLogExperienceDrawer variant={ExperiencePreviewVariant.DirectConfig} />
    </ListLayout>
  )
}
